const BASE_PATH = '/places';

/**
 * Address verification — the one group here that addresses no document.
 *
 * Nothing below reads or writes an administration record. These three routes
 * check what an address is before anybody stores one; the storing is still a
 * `patch` on a house or a company, exactly as it was when nothing checked.
 *
 * So there is no store over this and there should not be. A store holds a page
 * of a collection and these answer no collection — a caller asks a question and
 * uses the answer, the way `DocumentPicker` reaches the transport directly
 * rather than evicting a table's page to run its own search.
 *
 * **Every answer carries `configured`, and a deployment without an Amazon
 * Location key answers `false`.** That is a 200, not a refusal: it means this
 * deployment cannot look an address up, so the caller draws a form somebody can
 * type into rather than treating it as an error. `status()` answers the same
 * fact before anything is typed, which is when a form has to decide.
 *
 * **`address` and `geo` are optional in every answer, and their absence is the
 * answer.** The service never offers a partial address, because a partial
 * address is not storable — every field a house or a company requires has to be
 * there or none of it is. Test whether the key exists; never reach through it.
 */
export function placesMethods({ sendRequest, buildUrl }) {
    return {
        /**
         * Whether this deployment can look an address up at all.
         *
         * It reaches no vendor and bills nothing — the answer is the
         * deployment's own configuration. A form asks before its first
         * keystroke, because which controls it draws depends on the answer, and
         * learning the same fact by making a real lookup would pay for it.
         *
         * @returns {Promise<{ configured: boolean }>}
         */
        async status() {
            return await sendRequest(buildUrl(`${BASE_PATH}/status`), 'GET');
        },

        /**
         * Real addresses for a half-typed one, so the mistype never happens.
         *
         * Debouncing and a minimum length belong to whoever is typing, not
         * here: every call is billed, and this method cannot know how fast the
         * keystrokes arrived.
         *
         * @param {string} text - What has been typed so far.
         * @param {string} [country] - Two- or three-letter code confining the
         *   suggestions. Omitted entirely when not given.
         * @returns {Promise<{ configured: boolean, suggestions: Array<{ placeId: string, label: string }> }>}
         */
        async autocomplete(text, country) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/autocomplete`, { q: text, country }),
                'GET'
            );
        },

        /**
         * One suggestion resolved to the address and the point that get stored.
         *
         * `readPlace` rather than `read`, because every other noun's `read`
         * answers a document this platform owns and this one answers a fact
         * borrowed from a vendor.
         *
         * The identifier goes in the query string rather than the path, and
         * that is not cosmetic: a `PlaceId` is opaque vendor data that runs to
         * a couple of hundred characters, and a path parameter that long is
         * refused with `414 URI Too Long` before it reaches a handler.
         *
         * @param {string} placeId - What a suggestion carried.
         * @returns {Promise<{ configured: boolean, address?: Object, geo?: Object }>}
         */
        async readPlace(placeId) {
            return await sendRequest(
                buildUrl(BASE_PATH, { placeId }),
                'GET'
            );
        }
    };
}

export default placesMethods;
